import type { XpmDashboardSection } from './types'

export const XPM_PLATFORM_TYPE_ID = 2
export const FYI_PLATFORM_TYPE_ID = 8
export const DEFAULT_AGENT_BUILDER_PLATFORM_TYPE_ID = XPM_PLATFORM_TYPE_ID
export const DEFAULT_AGENT_BUILDER_PLATFORM_LABEL = 'XPM'

export type AgentBuilderPlatformOption = { value: number; label: string }

export const AGENT_BUILDER_PLATFORM_OPTIONS: AgentBuilderPlatformOption[] = [
  { value: 1, label: 'Xero' },
  { value: 2, label: 'XPM' },
  { value: 3, label: 'Xero WorkflowMax' },
  { value: 4, label: 'MYOB' },
  { value: 5, label: 'WorkGuru' },
  { value: 6, label: 'Bluerock Workflow Max' },
  { value: 7, label: 'Karbon' },
  { value: 8, label: 'FYI' },
  { value: 9, label: 'QuickBooks' },
]

export const XPM_DASHBOARD_SECTION_OPTIONS: Array<{ value: XpmDashboardSection; label: string }> = [
  { value: 'Billing', label: 'Billing' },
  { value: 'Profitability', label: 'Profitability' },
  { value: 'Job', label: 'Job' },
  { value: 'TeamMember', label: 'Team Member' },
  { value: 'WIP', label: 'WIP' },
  { value: 'Leads', label: 'Leads' },
  { value: 'Quotes', label: 'Quotes' },
  { value: 'DataIntegrity', label: 'Data Integrity' },
  { value: 'DiyReport', label: 'DIY Report' },
  { value: 'PurchaseOrders', label: 'Purchase Orders' },
  { value: 'Snapshot', label: 'Snapshot' },
  { value: 'TaxLodgements', label: 'Tax Lodgements' },
  { value: 'Xero', label: 'Xero' },
]

// FYI only carries permissions for these sections - see Group.NormalizePermissionFields,
// which forces every other section to "None" for FYI groups.
const FYI_DASHBOARD_SECTION_VALUES: XpmDashboardSection[] = [
  'Billing',
  'Profitability',
  'Job',
  'TeamMember',
  'WIP',
  'DataIntegrity',
  'DiyReport',
]

export const FYI_DASHBOARD_SECTION_OPTIONS = XPM_DASHBOARD_SECTION_OPTIONS
  .filter((option) => FYI_DASHBOARD_SECTION_VALUES.includes(option.value))

export function isXpmPlatform(platformTypeId: number | null | undefined) {
  return Number(platformTypeId) === XPM_PLATFORM_TYPE_ID
}

export function isFyiPlatform(platformTypeId: number | null | undefined) {
  return Number(platformTypeId) === FYI_PLATFORM_TYPE_ID
}

/**
 * Platforms whose Agent Builder dashboards are section-scoped and row-level secured.
 * Mirrors Platform.IsAgentBuilderSecuredType on the API.
 */
export function isSecuredAgentBuilderPlatform(platformTypeId: number | null | undefined) {
  return isXpmPlatform(platformTypeId) || isFyiPlatform(platformTypeId)
}

export function getDashboardSectionOptions(platformTypeId: number | null | undefined) {
  return isFyiPlatform(platformTypeId) ? FYI_DASHBOARD_SECTION_OPTIONS : XPM_DASHBOARD_SECTION_OPTIONS
}

/**
 * Resolves a section name against the sections the platform supports. Passing no
 * platformTypeId accepts any known section, which is what display-only paths want.
 */
export function normalizeDashboardSection(
  value: string | null | undefined,
  platformTypeId?: number | null,
): XpmDashboardSection | null {
  const normalized = String(value ?? '').trim().replace(/[\s_-]+/g, '').toLowerCase()
  const options = platformTypeId === undefined || platformTypeId === null
    ? XPM_DASHBOARD_SECTION_OPTIONS
    : getDashboardSectionOptions(platformTypeId)
  return options.find((option) => (
    option.value.toLowerCase() === normalized ||
    option.label.replace(/[\s_-]+/g, '').toLowerCase() === normalized
  ))?.value ?? null
}

export function sanitizeDashboardSection(
  value: string | null | undefined,
  platformTypeId: number | null | undefined,
): XpmDashboardSection | null {
  return isSecuredAgentBuilderPlatform(platformTypeId)
    ? normalizeDashboardSection(value, platformTypeId)
    : null
}

export function getAgentBuilderPlatformOptions(apiOptions: Array<{ value: number | string; name?: string; label?: string }>) {
  const knownLabels = new Map(AGENT_BUILDER_PLATFORM_OPTIONS.map((option) => [option.value, option.label]))
  const optionsById = new Map<number, AgentBuilderPlatformOption>()

  apiOptions.forEach((option) => {
    const value = Number(option.value)
    const label = String(option.name ?? option.label ?? knownLabels.get(value) ?? '').trim()
    if (!Number.isFinite(value) || !label) return
    optionsById.set(value, { value, label })
  })

  return Array.from(optionsById.values()).sort((left, right) => left.value - right.value)
}
